import type { TtqaPathRun } from '@kelilearning/psycheflow-contracts';
import type { TtqSession } from '../../services/ttqService';
import type { TtqibCanvasObject } from './types';
import { wholeObjectId } from './types';
import {
  TTQIB_CARD_WIDTH,
  TTQIB_COLUMN_GAP,
  TTQIB_LAYER_TOP,
  TTQIB_STACK_GAP,
  estimateWholeHeight,
} from './ttqibObjectLayout';
import { collectCttqConvergeParticleIds } from './ttqibParticleNumbers';

/** pathRun 源粒：优先 sourceParticleId，否则回退 sourceParticleIds[0] */
export function resolvePathRunSourceParticleId(run: {
  sourceParticleId?: string | null;
  sourceParticleIds?: string[] | null;
}): string {
  const preferred = String(run.sourceParticleId || '').trim();
  if (preferred) return preferred;
  for (const raw of run.sourceParticleIds ?? []) {
    const id = String(raw || '').trim();
    if (id) return id;
  }
  return '';
}

export type TtqDerivedFocus = {
  sourceParticleId: string;
  pathRunId?: string;
  questionChainVariantId?: string;
  anchorText?: string;
};

function isQuestionChainRun(run: TtqaPathRun): boolean {
  return run.ttqAngleId === 'question_chain'
    || run.ttqAngleId === 'questions_chain'
    || Boolean(run.questionChainVariantId)
    || run.sanyThinkingTag === '一题多题'
    || run.sanyThinkingTag === '多题多题';
}

export function listDoneQuestionChainRuns(session: TtqSession): TtqaPathRun[] {
  return (session.pathRuns ?? [])
    .filter((run) => run.status === 'done' && isQuestionChainRun(run))
    .slice()
    .sort((a, b) => String(a.completedAt || a.startedAt || '').localeCompare(String(b.completedAt || b.startedAt || '')));
}

export function listDerivedIdsForSource(session: TtqSession, sourceParticleId: string): string[] {
  const out: string[] = [];
  for (const run of listDoneQuestionChainRuns(session)) {
    if (resolvePathRunSourceParticleId(run) !== sourceParticleId) continue;
    for (const id of run.derivedParticleIds ?? []) {
      const trimmed = String(id || '').trim();
      if (trimmed && !out.includes(trimmed)) out.push(trimmed);
    }
  }
  return out;
}

export function listTastedAnchorTexts(session: TtqSession, sourceParticleId: string): string[] {
  const texts: string[] = [];
  for (const run of listDoneQuestionChainRuns(session)) {
    if (resolvePathRunSourceParticleId(run) !== sourceParticleId) continue;
    const anchor = String(run.expansionProvenance?.anchorText || '').trim();
    if (anchor && !texts.includes(anchor)) texts.push(anchor);
  }
  return texts;
}

import type { TtqPathRunModelAudit } from '@kelilearning/psycheflow-contracts';

export type TtqDerivedProvenanceView = {
  sourceParticleId: string;
  pathRunId: string;
  pathLabel: string;
  variantLabel: string;
  actionDisplayLabel: string;
  strategyLabel: string;
  anchorText?: string;
  customPrompt?: string;
  directionPreview?: string;
  /** 实际发给大模型的生成提示词 */
  generationPrompt?: string;
  /** 衍生缩略式：从解题关键步骤压出的精简 LaTeX */
  abbrExpr?: string;
  questionChainVariantId?: string;
  ttqAngleId?: string;
  pathId?: string;
  modelAudit?: TtqPathRunModelAudit | null;
  /** 多源行为（如收敛品题）的成员颗粒 */
  memberParticleIds?: string[];
};

export type TtqDerivedOriginPresentation = {
  headline: string;
  l1Label: string | null;
  l2Label: string | null;
  /** question_chain 用题面锚点；multi_solution 用解法路径 */
  detailKind: 'anchor' | 'approach' | 'none';
};

/** 顶栏居中：衍生来源 L1/L2 展示 */
export function resolveDerivedOriginPresentation(
  provenance: TtqDerivedProvenanceView | null | undefined,
): TtqDerivedOriginPresentation {
  if (!provenance) {
    return { headline: '衍生知识颗粒', l1Label: null, l2Label: null, detailKind: 'none' };
  }

  const angle = String(provenance.ttqAngleId || provenance.pathId || '').trim();
  const strategy = String(provenance.strategyLabel || '').trim();
  const variantId = String(provenance.questionChainVariantId || '').trim();

  if (angle.includes('multi_solution') || strategy === '一题多解' || (
    !variantId && String(provenance.actionDisplayLabel || '').includes('一题多解')
  )) {
    const l2 = strategy && strategy !== '一题多解' ? strategy : null;
    return {
      headline: '三一思考 · 一题多解',
      l1Label: '一题多解',
      l2Label: l2,
      detailKind: 'approach',
    };
  }

  if (
    angle.includes('question_chain')
    || Boolean(variantId)
    || String(provenance.actionDisplayLabel || '').includes('一题多题')
  ) {
    const l2 = strategy || variantId || null;
    return {
      headline: '三一思考 · 一题多题',
      l1Label: '一题多题',
      l2Label: l2 && l2 !== '一题多题' ? l2 : null,
      detailKind: 'anchor',
    };
  }

  if (angle.includes('cttq') || String(provenance.actionDisplayLabel || '').includes('收敛')) {
    return {
      headline: '收敛品题',
      l1Label: '收敛品题',
      l2Label: strategy && strategy !== '收敛品题' ? strategy : null,
      detailKind: 'none',
    };
  }

  return {
    headline: String(provenance.actionDisplayLabel || '衍生知识颗粒').trim() || '衍生知识颗粒',
    l1Label: strategy || null,
    l2Label: null,
    detailKind: 'none',
  };
}

export function isQuestionChainDerivedParticle(
  session: TtqSession,
  particleId: string,
): boolean {
  return listDoneQuestionChainRuns(session).some((run) => (
    (run.derivedParticleIds ?? []).includes(particleId)
  ));
}

/** 衍生粒是否来自源题的一题多解（multi_solution） */
export function isMultiSolutionDerivedParticle(
  session: TtqSession,
  particleId: string,
): boolean {
  const id = String(particleId || '').trim();
  if (!id) return false;
  return (session.pathRuns ?? []).some((run) => {
    if (run.status !== 'done') return false;
    const angle = String(run.ttqAngleId || run.pathId || '').trim();
    if (!angle.includes('multi_solution')) return false;
    return (run.derivedParticleIds ?? []).some((raw) => String(raw || '').trim() === id);
  });
}

/** 任一完成 pathRun 产出的衍生粒（与鸟瞰 derived 角色对齐） */
export function isSessionDerivedParticle(
  session: TtqSession,
  particleId: string,
): boolean {
  const id = String(particleId || '').trim();
  if (!id) return false;
  return (session.pathRuns ?? []).some((run) => (
    run.status === 'done'
    && (run.derivedParticleIds ?? []).some((raw) => String(raw || '').trim() === id)
  ));
}

/**
 * 原始颗粒下仍挂着的衍生 / 一解多题 / 收敛结果（完成 pathRun 产出且仍在会话中）。
 * 有依赖时暂不允许移出该原始颗粒。
 */
export function listSessionDescendantParticleIds(
  session: TtqSession,
  sourceParticleId: string,
): string[] {
  const sourceId = String(sourceParticleId || '').trim();
  if (!sourceId) return [];
  const present = new Set(
    (session.particles ?? [])
      .map((entry) => String(entry.particleId || '').trim())
      .filter(Boolean),
  );
  const out = new Set<string>();
  for (const run of session.pathRuns ?? []) {
    if (run.status !== 'done') continue;
    const sources = [
      String(run.sourceParticleId || '').trim(),
      ...((run.sourceParticleIds ?? []).map((id) => String(id || '').trim())),
    ].filter(Boolean);
    if (!sources.includes(sourceId)) continue;
    for (const raw of run.derivedParticleIds ?? []) {
      const id = String(raw || '').trim();
      if (!id || id === sourceId || !present.has(id)) continue;
      out.add(id);
    }
  }
  return Array.from(out);
}

export const ORIGINAL_PARTICLE_HAS_DESCENDANTS_TIP =
  '该原始知识颗粒下仍有衍生题、收敛题或一解多题，请先移出这些结果后再从品题中移除。';

/** 查找产出该衍生粒的完成 pathRun（最近优先） */
export function findPathRunForDerivedParticle(
  session: TtqSession,
  derivedParticleId: string,
): TtqaPathRun | null {
  const id = String(derivedParticleId || '').trim();
  if (!id) return null;
  const runs = (session.pathRuns ?? [])
    .filter((run) => run.status === 'done')
    .slice()
    .reverse();
  for (const run of runs) {
    if ((run.derivedParticleIds ?? []).some((raw) => String(raw || '').trim() === id)) {
      return run;
    }
  }
  return null;
}

export function resolveDerivedProvenance(
  session: TtqSession,
  derivedParticleId: string,
): TtqDerivedProvenanceView | null {
  const run = findPathRunForDerivedParticle(session, derivedParticleId);
  if (!run) return null;

  const provenance = run.expansionProvenance as unknown as TtqDerivedProvenanceView & {
    anchor_text?: string;
    custom_prompt?: string;
    strategyLabel?: string;
    actionDisplayLabel?: string;
    customPrompt?: string;
    anchorText?: string;
    directionPreview?: string;
    direction_preview?: string;
  } | null;
  const strategyLabel = String(provenance?.strategyLabel || run.pathLabel || '').trim();
  const variantLabel = String(run.pathLabel || strategyLabel || run.questionChainVariantId || '').trim();
  const anchorText = String(
    provenance?.anchorText || provenance?.anchor_text || '',
  ).trim() || undefined;
  const customPrompt = String(
    provenance?.customPrompt || provenance?.custom_prompt || '',
  ).trim() || undefined;
  const directionPreview = String(
    provenance?.directionPreview || provenance?.direction_preview || '',
  ).trim() || undefined;
  const generationPrompt = String(
    (provenance as { generationPrompt?: string } | null)?.generationPrompt || '',
  ).trim() || undefined;
  const abbrExpr = String(
    (provenance as { abbrExpr?: string } | null)?.abbrExpr || '',
  ).trim() || undefined;
  const angle = String(run.ttqAngleId || run.pathId || '').trim();
  const isQuestionChain = angle.includes('question_chain') || Boolean(run.questionChainVariantId);
  const memberParticleIds = (run.sourceParticleIds ?? [])
    .map((id) => String(id || '').trim())
    .filter(Boolean);
  return {
    sourceParticleId: resolvePathRunSourceParticleId(run),
    pathRunId: run.id,
    pathLabel: String(run.pathLabel || '').trim(),
    variantLabel,
    actionDisplayLabel: String(
      provenance?.actionDisplayLabel
      || (isQuestionChain ? `一题多题 · ${variantLabel}` : run.pathLabel || '衍生来源'),
    ).trim(),
    strategyLabel: strategyLabel || variantLabel,
    anchorText,
    customPrompt,
    directionPreview,
    generationPrompt,
    abbrExpr,
    questionChainVariantId: run.questionChainVariantId || undefined,
    ttqAngleId: run.ttqAngleId || undefined,
    pathId: run.pathId || undefined,
    modelAudit: run.modelAudit || null,
    memberParticleIds: memberParticleIds.length >= 2 ? memberParticleIds : undefined,
  };
}

/** 从知识颗粒 metadata.ttqWorkbench 补全来源线索（pathRun 缺字段时） */
export function mergeProvenanceFromParticleMetadata(
  base: TtqDerivedProvenanceView | null | undefined,
  metadata: unknown,
): TtqDerivedProvenanceView | null {
  const meta = metadata && typeof metadata === 'object'
    ? metadata as Record<string, unknown>
    : null;
  const workbench = meta?.ttqWorkbench && typeof meta.ttqWorkbench === 'object'
    ? meta.ttqWorkbench as Record<string, unknown>
    : null;
  const expansion = workbench?.expansionProvenance && typeof workbench.expansionProvenance === 'object'
    ? workbench.expansionProvenance as Record<string, unknown>
    : null;

  if (!base && !expansion) return null;

  const anchorText = String(
    base?.anchorText
    || expansion?.anchorText
    || expansion?.anchor_text
    || '',
  ).trim() || undefined;
  const customPrompt = String(
    base?.customPrompt
    || expansion?.customPrompt
    || expansion?.custom_prompt
    || '',
  ).trim() || undefined;
  const directionPreview = String(
    base?.directionPreview
    || expansion?.directionPreview
    || expansion?.direction_preview
    || '',
  ).trim() || undefined;
  const generationPrompt = String(
    base?.generationPrompt
    || expansion?.generationPrompt
    || '',
  ).trim() || undefined;
  const abbrExpr = String(
    base?.abbrExpr
    || workbench?.abbrExpr
    || expansion?.abbrExpr
    || '',
  ).trim() || undefined;
  const strategyLabel = String(
    base?.strategyLabel || expansion?.strategyLabel || '',
  ).trim();
  const actionDisplayLabel = String(
    base?.actionDisplayLabel || expansion?.actionDisplayLabel || '',
  ).trim();
  const variantLabel = String(base?.variantLabel || strategyLabel || '').trim();

  const memberFromMeta = Array.isArray(workbench?.memberParticleIds)
    ? workbench.memberParticleIds.map((id) => String(id || '').trim()).filter(Boolean)
    : [];
  const memberParticleIds = (base?.memberParticleIds && base.memberParticleIds.length >= 2
    ? base.memberParticleIds
    : memberFromMeta.length >= 2
      ? memberFromMeta
      : undefined);

  return {
    sourceParticleId: String(
      base?.sourceParticleId || expansion?.sourceParticleId || '',
    ).trim(),
    pathRunId: base?.pathRunId || String(workbench?.pathRunId || '').trim(),
    pathLabel: base?.pathLabel || '',
    variantLabel,
    actionDisplayLabel: actionDisplayLabel || (variantLabel ? `一题多题 · ${variantLabel}` : '衍生来源'),
    strategyLabel: strategyLabel || variantLabel,
    anchorText,
    customPrompt,
    directionPreview: directionPreview || base?.directionPreview,
    generationPrompt: generationPrompt || base?.generationPrompt,
    abbrExpr: abbrExpr || base?.abbrExpr,
    questionChainVariantId: base?.questionChainVariantId
      || String(expansion?.questionChainVariantId || workbench?.questionChainVariantId || '').trim()
      || undefined,
    ttqAngleId: base?.ttqAngleId,
    pathId: base?.pathId,
    modelAudit: (base?.modelAudit
      || (workbench?.modelAudit && typeof workbench.modelAudit === 'object'
        ? workbench.modelAudit as TtqPathRunModelAudit
        : null)) || null,
    memberParticleIds,
  };
}

/** 段文本是否属于已品线索（锚点包含或相等） */
export function isSegmentTasted(segmentText: string, tastedAnchors: readonly string[]): boolean {
  const text = segmentText.replace(/\s+/g, '').trim();
  if (!text) return false;
  return tastedAnchors.some((anchor) => {
    const key = anchor.replace(/\s+/g, '').trim();
    if (!key) return false;
    return key.includes(text) || text.includes(key);
  });
}

/** 一题多题衍生一律可见（按生成顺序叠在源题下）；focus 仅用于定位，不隐藏其它方向 */
export function resolveVisibleDerivedIds(
  session: TtqSession,
  _focus?: TtqDerivedFocus | null,
): Set<string> {
  const visible = new Set<string>();
  for (const run of listDoneQuestionChainRuns(session)) {
    for (const id of run.derivedParticleIds ?? []) {
      const trimmed = String(id || '').trim();
      if (trimmed) visible.add(trimmed);
    }
  }
  return visible;
}

/**
 * 一对多画布布局：
 * - 原始颗粒强制横向并排（锁定同一行）
 * - 任一完成 pathRun 的衍生颗粒叠在对应源题下方
 */
export function applyDerivedStackAndVisibility(
  session: TtqSession,
  objects: TtqibCanvasObject[],
  _focus?: TtqDerivedFocus | null,
): TtqibCanvasObject[] {
  const particles = session.particles ?? [];
  const byId = new Map(particles.map((entry) => [entry.particleId, entry]));
  const convergeIds = collectCttqConvergeParticleIds(session);

  const derivedToSource = new Map<string, string>();
  const derivedOfSource = new Map<string, string[]>();
  const doneRuns = (session.pathRuns ?? [])
    .filter((run) => run.status === 'done')
    .slice()
    .sort((a, b) => String(a.completedAt || a.startedAt || '').localeCompare(String(b.completedAt || b.startedAt || '')));

  for (const run of doneRuns) {
    if (
      run.pathId === 'cttq_converge'
      || run.ttqAngleId === 'cttq_converge'
      || run.pathId === 'pattern_unify'
      || run.ttqAngleId === 'pattern_unify'
    ) {
      continue;
    }
    const sourceId = resolvePathRunSourceParticleId(run);
    if (!sourceId) continue;
    for (const rawId of run.derivedParticleIds ?? []) {
      const derivedId = String(rawId || '').trim();
      if (!derivedId || derivedId === sourceId || convergeIds.has(derivedId)) continue;
      if (!derivedToSource.has(derivedId)) {
        derivedToSource.set(derivedId, sourceId);
      }
      if (derivedToSource.get(derivedId) !== sourceId) continue;
      const bucket = derivedOfSource.get(sourceId) ?? [];
      if (!bucket.includes(derivedId)) bucket.push(derivedId);
      derivedOfSource.set(sourceId, bucket);
    }
  }

  const originals = particles
    .map((entry) => entry.particleId)
    .filter((id) => !derivedToSource.has(id) && !convergeIds.has(id));

  const wholeByParticle = new Map(
    objects.filter((item) => item.type === 'particle-whole').map((item) => [item.particleId, item]),
  );

  const nextPos = new Map<string, { x: number; y: number; layerId: string; width?: number }>();
  const mainLayerId = objects.find((item) => item.type === 'particle-whole')?.layerId
    || 'layer:main';

  // 原始：固定横向并排
  originals.forEach((particleId, index) => {
    const whole = wholeByParticle.get(particleId);
    nextPos.set(particleId, {
      x: 80 + index * TTQIB_COLUMN_GAP,
      y: TTQIB_LAYER_TOP,
      layerId: whole?.layerId || mainLayerId,
      width: whole?.width ?? TTQIB_CARD_WIDTH,
    });
  });

  // 衍生：各源列向下延伸（源不在会话则挂孤儿列）
  const orphanIds: string[] = [];
  for (const [derivedId, sourceId] of derivedToSource) {
    if (!byId.has(derivedId)) continue;
    if (!byId.has(sourceId)) {
      orphanIds.push(derivedId);
      continue;
    }
  }

  for (const sourceId of originals) {
    const sourcePos = nextPos.get(sourceId);
    const sourceWhole = wholeByParticle.get(sourceId);
    if (!sourcePos) continue;
    const derivedIds = (derivedOfSource.get(sourceId) ?? []).filter((id) => byId.has(id));
    // 与 cardRectById 一致：实测偏矮时仍按 estimate 占位，避免衍生卡压住源卡
    let cursorY = sourcePos.y
      + Math.max(sourceWhole?.height ?? 0, estimateWholeHeight(byId.get(sourceId)))
      + TTQIB_STACK_GAP;
    for (const derivedId of derivedIds) {
      const derivedWhole = wholeByParticle.get(derivedId);
      nextPos.set(derivedId, {
        x: sourcePos.x,
        y: cursorY,
        layerId: sourcePos.layerId,
        width: sourcePos.width ?? TTQIB_CARD_WIDTH,
      });
      cursorY += Math.max(
        derivedWhole?.height ?? 0,
        estimateWholeHeight(byId.get(derivedId)),
      ) + TTQIB_STACK_GAP;
    }
  }

  if (orphanIds.length > 0) {
    const orphanX = 80 + originals.length * TTQIB_COLUMN_GAP;
    let cursorY = TTQIB_LAYER_TOP;
    for (const derivedId of orphanIds) {
      const derivedWhole = wholeByParticle.get(derivedId);
      nextPos.set(derivedId, {
        x: orphanX,
        y: cursorY,
        layerId: derivedWhole?.layerId || mainLayerId,
        width: derivedWhole?.width ?? TTQIB_CARD_WIDTH,
      });
      cursorY += Math.max(
        derivedWhole?.height ?? 0,
        estimateWholeHeight(byId.get(derivedId)),
      ) + TTQIB_STACK_GAP;
    }
  }

  return objects.map((object) => {
    const stacked = nextPos.get(object.particleId);
    if (!stacked) {
      return object.type === 'particle-whole' || object.hidden
        ? { ...object, hidden: false }
        : object;
    }
    return {
      ...object,
      layerId: stacked.layerId,
      x: stacked.x,
      y: stacked.y,
      width: object.type === 'particle-whole' ? stacked.width : object.width,
      hidden: false,
    };
  });
}

/** 从爆炸选段激活对应品题线索焦点 */
export function resolveFocusFromSegment(
  session: TtqSession,
  sourceParticleId: string,
  segmentText: string,
): TtqDerivedFocus | null {
  const text = segmentText.replace(/\s+/g, '').trim();
  if (!text) return null;
  const runs = listDoneQuestionChainRuns(session)
    .filter((run) => resolvePathRunSourceParticleId(run) === sourceParticleId)
    .reverse();
  for (const run of runs) {
    const anchor = String(run.expansionProvenance?.anchorText || '').replace(/\s+/g, '');
    if (!anchor) continue;
    if (anchor.includes(text) || text.includes(anchor)) {
      return {
        sourceParticleId,
        pathRunId: run.id,
        questionChainVariantId: run.questionChainVariantId || undefined,
        anchorText: run.expansionProvenance?.anchorText || segmentText,
      };
    }
  }
  return null;
}
